import { patchHTML } from "../core/surface-host.js";
import { ensureAdminContentLayout } from "./layout.js";
import { escapeHTML, renderSkeleton, timeAgo, truncateIDText as renderTruncatedIDText } from "../render/ui-helpers.js";

export function renderTurnsRoute(layoutRoot, state, options = {}) {
  ensureAdminContentLayout(layoutRoot, "turns", `
    <div class="diagnostics-page">
      <div data-turns-header-root></div>
      <div data-turns-create-root></div>
      <div data-turns-content-root></div>
    </div>
  `);
  const headerRoot = layoutRoot.querySelector("[data-turns-header-root]");
  const createRoot = layoutRoot.querySelector("[data-turns-create-root]");
  const contentRoot = layoutRoot.querySelector("[data-turns-content-root]");
  if (!state.apiKey) {
    patchHTML(headerRoot, "");
    patchHTML(createRoot, "");
    patchHTML(contentRoot, options.renderLockedState?.() || "");
    return;
  }
  if (state.loading) {
    patchHTML(headerRoot, "");
    patchHTML(createRoot, "");
    patchHTML(contentRoot, renderSkeleton(4));
    return;
  }
  const pageState = buildTurnsPageState(state);
  patchHTML(headerRoot, renderTurnsHeaderSection(pageState));
  patchHTML(createRoot, renderTurnsCreateSection(pageState, options));
  patchHTML(contentRoot, renderTurnsContentSection(state, pageState, options));
}

export function buildTurnsPageState(state) {
  const runnable = state.agents.some((item) => String(item.execution_mode || "harness") === "harness") && state.workspaces.length > 0;
  const standaloneTurns = state.turns.filter((item) => String(item.thread_id || "").trim() === "");
  const pendingDecisions = state.pendingDecisions;
  const recentPromotions = state.promotions.slice(0, 8);
  const runningTurns = state.turns.filter((item) => item.state === "running");
  const failedTurns = state.turns.filter((item) => item.state === "failed" || item.state === "canceled");
  const filter = state.diagnosticFilter;
  const counts = {
    all: state.turns.length,
    running: runningTurns.length,
    decisions: pendingDecisions.length,
    promotions: state.promotions.length,
    failed: failedTurns.length,
    standalone: standaloneTurns.length
  };
  const filteredTurns = filter === "running" ? runningTurns
    : filter === "failed" ? failedTurns
      : filter === "standalone" ? standaloneTurns
        : state.turns;
  const filters = [
    { id: "all", label: "All turns", count: counts.all },
    { id: "running", label: "Running", count: counts.running },
    { id: "decisions", label: "Decisions", count: counts.decisions },
    { id: "promotions", label: "Promotions", count: counts.promotions },
    { id: "failed", label: "Failed", count: counts.failed },
    { id: "standalone", label: "Standalone", count: counts.standalone }
  ];
  const showCreate = state.diagnosticCreateOpen && runnable;
  return { runnable, pendingDecisions, recentPromotions, filter, filteredTurns, filters, showCreate };
}

export function renderTurnsHeaderSection(pageState) {
  return `
    <div class="threads-header">
      <div class="threads-filter-bar" role="tablist">
        ${pageState.filters.map((item) => `
          <button class="threads-filter__item ${item.id === pageState.filter ? "threads-filter__item--active" : ""} ${(item.id === "decisions" || item.id === "running") && item.count > 0 ? "threads-filter__item--attention" : ""}" data-diagnostic-filter="${escapeHTML(item.id)}">
            ${escapeHTML(item.label)} <span class="threads-filter__count">${item.count}</span>
          </button>
        `).join("")}
      </div>
      ${pageState.runnable ? `<button class="button button--sm ${pageState.showCreate ? "button--ghost" : "button--primary"}" type="button" data-action="toggle-diagnostic-create">${pageState.showCreate ? "Cancel" : "+ Ad hoc"}</button>` : ""}
    </div>
  `;
}

export function renderTurnsCreateSection(pageState, options = {}) {
  if (!pageState.showCreate) {
    return "";
  }
  return `
    <div class="entity-form-panel">
      ${options.renderTurnForm?.() || ""}
    </div>
  `;
}

export function renderTurnsContentSection(state, pageState, options = {}) {
  if (pageState.filter === "decisions") {
    return `
      <div class="diagnostics-section">
        ${pageState.pendingDecisions.length ? `
          <div class="entity-list">
            ${pageState.pendingDecisions.map((item) => options.renderPendingDecisionGlance?.(item) || "").join("")}
          </div>
        ` : `<div class="diagnostics-empty">No pending decisions.</div>`}
      </div>
    `;
  }
  if (pageState.filter === "promotions") {
    return `
      <div class="diagnostics-section">
        ${pageState.recentPromotions.length ? `
          <div class="entity-list">
            ${pageState.recentPromotions.map((item) => options.renderPromotionDiagnosticCard?.(item) || "").join("")}
          </div>
        ` : `<div class="diagnostics-empty">No promotions yet.</div>`}
      </div>
    `;
  }
  return `
    ${pageState.pendingDecisions.length && pageState.filter === "all" ? `
      <div class="diagnostics-callout diagnostics-callout--warn">
        <span class="diagnostics-callout__label">${pageState.pendingDecisions.length} decision${pageState.pendingDecisions.length > 1 ? "s" : ""} pending</span>
        <button class="button button--sm button--ghost" type="button" data-diagnostic-filter="decisions">View</button>
      </div>
    ` : ""}
    ${pageState.recentPromotions.length && pageState.filter === "all" ? `
      <div class="diagnostics-callout">
        <span class="diagnostics-callout__label">${pageState.recentPromotions.length} recent promotion${pageState.recentPromotions.length > 1 ? "s" : ""}</span>
        <button class="button button--sm button--ghost" type="button" data-diagnostic-filter="promotions">View</button>
      </div>
    ` : ""}
    <div class="entity-list">
      ${pageState.filteredTurns.length ? pageState.filteredTurns.map((item) => renderTurnRow(state, item, false, options)).join("") : `
        <div class="diagnostics-empty">${pageState.filter === "all" ? "No turns yet." : `No ${pageState.filter} turns.`}</div>
      `}
    </div>
  `;
}

export function renderTurnRow(state, item, compact, options = {}) {
  const selected = state.route.id === "turn-detail" && state.route.params.turnID === item.id;
  const isThreaded = String(item.thread_id || "").trim() !== "";
  const ago = timeAgo(item.created_at);
  const borderTone = item.state === "running" ? "running" : item.state === "failed" || item.state === "canceled" ? "danger" : item.wait_state === "awaiting_decision" ? "input" : item.state === "succeeded" ? "ready" : "default";
  const lookupName = typeof options.lookupName === "function" ? options.lookupName : (_items, id) => id || "unknown";
  const title = item.mode === "llm"
    ? `${lookupName(state.agents, item.agent_id)} \u00b7 LLM`
    : `${lookupName(state.agents, item.agent_id)} &rarr; ${lookupName(state.workspaces, item.workspace_id)}`;
  const preview = item.mode === "llm"
    ? compactPrompt(item.assistant_message || item.prompt)
    : compactPrompt(item.prompt);
  return `
    <a class="run-row thread-row--border-${escapeHTML(borderTone)} ${selected ? "run-row--selected" : ""}" href="/turns/${encodeURIComponent(item.id)}" data-route>
      <div class="run-row__main">
        <div class="entity-row__body">
          <div class="run-row__title">${escapeHTML(title)}</div>
          <div class="run-row__meta">${escapeHTML(preview)}</div>
          ${item.wait_state === "awaiting_decision" ? `<div class="run-row__signal">Waiting on decision${item.pending_decision_summary ? ` · ${escapeHTML(item.pending_decision_summary)}` : ""}</div>` : ""}
          ${!compact ? `<div class="entity-row__detail">${renderTruncatedIDText(item.id, 10)}${isThreaded ? ` · thread` : " · standalone"}${item.mode === "llm" ? " · llm" : ""}${item.branch_name ? ` · ${escapeHTML(item.branch_name)}` : ""}</div>` : ""}
        </div>
        <div class="thread-row__side">
          <span class="badge badge--${escapeHTML(item.mode || "harness")}">${escapeHTML((item.mode || "harness").toUpperCase())}</span>
          <span class="badge badge--${escapeHTML(item.state)}">${escapeHTML(item.state)}</span>
          ${ago ? `<span class="thread-row__time">${escapeHTML(ago)}</span>` : ""}
        </div>
      </div>
    </a>
  `;
}

function compactPrompt(value) {
  const trimmed = String(value || "").trim();
  if (trimmed.length <= 80) {
    return trimmed || "No prompt";
  }
  return `${trimmed.slice(0, 77)}...`;
}
